import { useEffect, useMemo, useRef, useState } from "react";
import {
  getWheelConfig,
  spinWheel,
  ApiError,
  type WheelSegment,
} from "./api.ts";
import { haptic } from "./telegram.ts";

/**
 * Prize wheel shown on the confirmation of a qualifying order (total ≥ the
 * server's spin threshold). The server owns the outcome: we fetch the segment
 * list, POST to spin, then animate the wheel so the server-chosen segment lands
 * under the pointer. One spin per order (the API enforces it).
 */

const SIZE = 260;
const R = 120;
const CX = SIZE / 2;
const CY = SIZE / 2;
const EXTRA_SPINS = 5; // full turns before settling, for drama

function pointFor(angleDeg: number, radius: number): [number, number] {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return [CX + radius * Math.cos(rad), CY + radius * Math.sin(rad)];
}

function slicePath(startDeg: number, endDeg: number): string {
  const [x1, y1] = pointFor(startDeg, R);
  const [x2, y2] = pointFor(endDeg, R);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${CX} ${CY} L ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}

type Phase = "idle" | "spinning" | "done";

export function Wheel({
  orderId,
  alreadyWonLabel,
}: {
  orderId: string;
  /** If the order was already spun, show its result immediately (no re-spin). */
  alreadyWonLabel?: string | null;
}) {
  const [segments, setSegments] = useState<WheelSegment[] | null>(null);
  const [rotation, setRotation] = useState(0);
  const [phase, setPhase] = useState<Phase>(alreadyWonLabel ? "done" : "idle");
  const [wonLabel, setWonLabel] = useState<string | null>(
    alreadyWonLabel ?? null,
  );
  const [error, setError] = useState<string | null>(null);
  const spinningRef = useRef(false);

  useEffect(() => {
    getWheelConfig()
      .then(setSegments)
      .catch(() => setSegments([]));
  }, []);

  const seg = segments && segments.length ? 360 / segments.length : 0;

  const paths = useMemo(() => {
    if (!segments) return [];
    return segments.map((s, i) => ({
      d: slicePath(i * seg, (i + 1) * seg),
      color: s.color,
      label: s.label,
      mid: (i + 0.5) * seg,
    }));
  }, [segments, seg]);

  const spin = async () => {
    if (!segments || spinningRef.current || phase === "done") return;
    spinningRef.current = true;
    setError(null);
    setPhase("spinning");
    haptic("medium");
    try {
      const result = await spinWheel(orderId);
      const idx = segments.findIndex((s) => s.id === result.wonId);
      const target = idx >= 0 ? idx : 0;
      // Land the target slice's centre under the top pointer, after full spins.
      const final = EXTRA_SPINS * 360 - (target + 0.5) * seg;
      setRotation(final);
      setWonLabel(result.label);
      window.setTimeout(() => {
        setPhase("done");
        haptic("success");
      }, 4200);
    } catch (e) {
      spinningRef.current = false;
      setPhase("idle");
      setError(e instanceof ApiError ? e.message : "spin failed");
    }
  };

  if (segments && segments.length === 0 && phase !== "done") {
    return null; // config failed to load; hide gracefully
  }

  return (
    <section className="card wheel-card">
      <h2 className="gate-title">🎡 Wheel of Fortune</h2>
      <p className="gate-text">
        {phase === "done"
          ? "Here's your prize — we'll apply it to your next order."
          : "Your order qualifies for a free spin. Good luck!"}
      </p>

      <div className="wheel-stage">
        <div className="wheel-pointer" />
        <svg
          className="wheel-svg"
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          style={{
            transform: `rotate(${rotation}deg)`,
            transition:
              phase === "spinning"
                ? "transform 4s cubic-bezier(0.15, 0.9, 0.25, 1)"
                : "none",
          }}
        >
          {paths.map((p, i) => (
            <g key={i}>
              <path d={p.d} fill={p.color} stroke="#fff" strokeWidth={2} />
              <text
                x={pointFor(p.mid, R * 0.62)[0]}
                y={pointFor(p.mid, R * 0.62)[1]}
                fill="#fff"
                fontSize="10"
                fontWeight="700"
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${p.mid}, ${pointFor(p.mid, R * 0.62)[0]}, ${pointFor(p.mid, R * 0.62)[1]})`}
              >
                {p.label}
              </text>
            </g>
          ))}
          <circle cx={CX} cy={CY} r={20} fill="#fff" />
          <circle cx={CX} cy={CY} r={13} fill="url(#hub)" />
          <defs>
            <linearGradient id="hub" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#ff2e93" />
              <stop offset="1" stopColor="#ff7a3d" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {phase === "done" && wonLabel && (
        <div className="wheel-prize">🎉 You won: <strong>{wonLabel}</strong></div>
      )}

      {error && <p className="note error">{error}</p>}

      {phase !== "done" && (
        <button className="btn" onClick={spin} disabled={phase === "spinning"}>
          {phase === "spinning" ? "Spinning…" : "SPIN 🎯"}
        </button>
      )}
    </section>
  );
}
